import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  FiArrowRight, FiShoppingCart, FiMinus, FiPlus, FiPackage,
  FiCheck, FiAlertTriangle, FiChevronLeft, FiChevronRight,
  FiTag, FiTruck, FiShield, FiInfo, FiHeadphones, FiCreditCard,
  FiLock, FiGlobe, FiAward, FiShare2, FiX, FiHeart, FiClock,
  FiGrid, FiExternalLink
} from "react-icons/fi";
import { useCart, useCustomer } from "../context/ShopContext";
import ShopTopBar from "../components/ShopTopBar";
import "./ShopDetail.css";

/* ─────────────────────────────────────────────────────────────
   IMAGE HELPER — mirrors Shop.jsx logic exactly
   Always resolves via API URL (no webpack bundle lookup)
   ───────────────────────────────────────────────────────────── */
function shopImg(filename) {
  if (!filename) return null;
  return `${process.env.REACT_APP_API_URL}/uploads/shop/${filename}`;
}

/* ── Strip HTML tags ── */
function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
}

/* ─────────────────────────────────────────────────────────────
   RECENTLY VIEWED — localStorage helpers
   ───────────────────────────────────────────────────────────── */
const RV_KEY   = "noel_recently_viewed";
const RV_MAX   = 10;
const RV_SHOW  = 5;

function rvRead() {
  try {
    const raw = localStorage.getItem(RV_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function rvWrite(items) {
  try { localStorage.setItem(RV_KEY, JSON.stringify(items)); } catch {}
}

function rvPush(product) {
  const stub = {
    id:               product.id,
    slug:             product.slug,
    name:             product.name,
    price:            product.price,
    sale_price:       product.sale_price,
    image1:           product.image1,
    category_name:    product.category_name,
    category_slug:    product.category_slug,
    quantity:         product.quantity,
    discount_percent: product.discount_percent,
  };
  const existing = rvRead().filter(p => p.id !== product.id);
  rvWrite([stub, ...existing].slice(0, RV_MAX));
}

/* ─────────────────────────────────────────────────────────────
   SOCIAL ICON
   ───────────────────────────────────────────────────────────── */
function SocialIcon({ platform }) {
  const p = (platform || "").toLowerCase();
  if (p.includes("facebook"))  return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>;
  if (p.includes("instagram")) return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>;
  if (p.includes("twitter") || p.includes("x.com")) return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
  if (p.includes("youtube"))   return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.95C5.12 20 12 20 12 20s6.88 0 8.59-.47a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon fill="#fff" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/></svg>;
  if (p.includes("linkedin"))  return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>;
  if (p.includes("whatsapp"))  return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>;
  if (p.includes("tiktok"))    return <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg>;
  return <FiGlobe size={16} />;
}

const fmt = (n) =>
  "R " + Number(n).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const WHATSAPP_NUMBER = "27829362678";
const ORDERS_EMAIL    = "orders@noelautomotiverepaires.com";

const TRUST_ITEMS = [
  { icon: <FiHeadphones size={22} />, title: "Technical Support",    sub: "In-House support"               },
  { icon: <FiCreditCard size={22} />, title: "Payment Method",       sub: "Electronic Funds Transfer (EFT)" },
  { icon: <FiLock      size={22} />, title: "Secured Payment",      sub: "100% secured"                   },
  { icon: <FiTruck     size={22} />, title: "Nationwide Shipping",   sub: "Shipping To All Regions"        },
  { icon: <FiAward     size={22} />, title: "Warranty",              sub: "Warranty Policy On All Items"   },
];

const CAR_MAKES = [
  "Toyota","Volkswagen","Ford","Hyundai","Kia","Nissan",
  "BMW","Renault","Honda","Suzuki","Mazda","Audi",
  "Mercedes-Benz","Chevrolet","Peugeot","Opel","Mitsubishi",
  "Isuzu","Land Rover","Volvo","Subaru","Jeep","Fiat",
];

/* ──────────────────────────────────────
   WISHLIST HOOK
   ────────────────────────────────────── */
function useWishlist() {
  const { customer, token } = useCustomer();
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [pending,     setPending]     = useState(new Set());

  useEffect(() => {
    if (!customer || !token) { setWishlistIds(new Set()); return; }
    fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/wishlist`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setWishlistIds(new Set(data.map(p => p.product_id)));
        }
      })
      .catch(() => {});
  }, [customer, token]);

  const toggle = useCallback(async (productId) => {
    if (!customer || !token) return;
    if (pending.has(productId)) return;

    const alreadyIn = wishlistIds.has(productId);

    setWishlistIds(prev => {
      const next = new Set(prev);
      alreadyIn ? next.delete(productId) : next.add(productId);
      return next;
    });
    setPending(prev => new Set(prev).add(productId));

    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/customers/me/wishlist/${productId}`,
        {
          method:  alreadyIn ? "DELETE" : "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch {
      setWishlistIds(prev => {
        const next = new Set(prev);
        alreadyIn ? next.add(productId) : next.delete(productId);
        return next;
      });
    } finally {
      setPending(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [customer, token, wishlistIds, pending]);

  return { wishlistIds, toggle, isLoggedIn: !!customer };
}

/* ══════════════════════════════════════
   WISHLIST HEART BUTTON
   ══════════════════════════════════════ */
function WishlistBtn({ productId, wishlistIds, toggle, isLoggedIn }) {
  const inWishlist = wishlistIds.has(productId);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(productId);
  };

  return (
    <button
      className={`sd__wishlist-btn${inWishlist ? " sd__wishlist-btn--active" : ""}`}
      onClick={handleClick}
      aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
      title={isLoggedIn
        ? (inWishlist ? "Remove from wishlist" : "Add to wishlist")
        : "Login to save to wishlist"}
    >
      <FiHeart size={16} />
      <span>{inWishlist ? "Saved" : "Save to Wishlist"}</span>
    </button>
  );
}

/* ══════════════════════════════════════
   MINI PRODUCT CARD
   ══════════════════════════════════════ */
function MiniCard({ product, variant = "rv" }) {
  const price    = Number(product.sale_price || product.price);
  const oldPrice = product.sale_price ? Number(product.price) : null;
  const outOfStock = product.quantity < 1;

  return (
    <Link to={`/shop/${product.slug}`} className={`sd__mini-card sd__mini-card--${variant}`}>
      <div className="sd__mini-img-wrap">
        {product.image1 ? (
          <img
            src={shopImg(product.image1)}
            alt={product.name}
            className="sd__mini-img"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="sd__mini-no-img"><FiPackage size={28} /></div>
        )}
        {product.discount_percent > 0 && (
          <span className="sd__mini-badge">-{Math.round(product.discount_percent)}%</span>
        )}
        {outOfStock && (
          <span className="sd__mini-oos">Out of stock</span>
        )}
      </div>

      <div className="sd__mini-info">
        {product.category_name && (
          <span className="sd__mini-cat">{product.category_name}</span>
        )}
        <p className="sd__mini-name">{product.name}</p>
        <div className="sd__mini-price-row">
          <span className="sd__mini-price">{fmt(price)}</span>
          {oldPrice && (
            <span className="sd__mini-old">{fmt(oldPrice)}</span>
          )}
        </div>
        {(product.car_make || product.car_model) && (
          <p className="sd__mini-fitment">
            {product.car_make} {product.car_model}
            {product.car_year_from ? ` · ${product.car_year_from}` : ""}
          </p>
        )}
      </div>
    </Link>
  );
}

/* ══════════════════════════════════════
   LIGHTBOX MODAL
   ══════════════════════════════════════ */
function LightboxModal({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  const prev = (e) => {
    e.stopPropagation();
    setIdx(i => (i - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setIdx(i => (i + 1) % images.length);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowLeft")   setIdx(i => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight")  setIdx(i => (i + 1) % images.length);
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [images.length, onClose]);

  return (
    <div className="sd__lb-backdrop" onClick={onClose}>
      <button className="sd__lb-close" onClick={onClose} aria-label="Close lightbox">
        <FiX size={24} />
      </button>

      {images.length > 1 && (
        <button className="sd__lb-nav sd__lb-nav--prev" onClick={prev} aria-label="Previous image">
          <FiChevronLeft size={28} />
        </button>
      )}

      <div className="sd__lb-img-wrap" onClick={(e) => e.stopPropagation()}>
        <img
          src={shopImg(images[idx])}
          alt={`Product image ${idx + 1}`}
          className="sd__lb-img"
        />
        {images.length > 1 && (
          <div className="sd__lb-counter">{idx + 1} / {images.length}</div>
        )}
      </div>

      {images.length > 1 && (
        <button className="sd__lb-nav sd__lb-nav--next" onClick={next} aria-label="Next image">
          <FiChevronRight size={28} />
        </button>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   WHATSAPP ICON (inline SVG)
   ══════════════════════════════════════ */
function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
    </svg>
  );
}

/* ══════════════════════════════════════
   PAYMENT INSTRUCTIONS PANEL
   Displayed below the product images column (desktop)
   and after the product details tab section (mobile)
   ══════════════════════════════════════ */
function PaymentInstructionsPanel() {
  return (
    <div className="sd__pop-panel">
      <div className="sd__pop-panel__header">
        <FiCreditCard size={15} />
        <span>How to Pay</span>
      </div>

      <div className="sd__pop-panel__body">
        <table className="sd__pop-table">
          <tbody>
            <tr>
              <th>Step 1</th>
              <td>
                Complete your payment via <strong>Instant EFT</strong> using our banking
                details shown on Checkout screen and displayed after Order completion. Use your <strong>order reference number</strong> as the payment reference.
              </td>
            </tr>
            <tr>
              <th>Step 2</th>
              <td>
                Send your proof of payment via <strong>WhatsApp</strong> or{" "}
                <strong>Email</strong>:
              </td>
            </tr>
            <tr>
              <th>WhatsApp</th>
              <td>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20please%20find%20my%20proof%20of%20payment%20attached.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sd__pop-link sd__pop-link--whatsapp"
                >
                  <WhatsAppIcon />
                  +27 82 936 2678
                  <FiExternalLink size={11} />
                </a>
              </td>
            </tr>
            <tr>
              <th>Email</th>
              <td>
                <a
                  href={`mailto:${ORDERS_EMAIL}?subject=Proof%20of%20Payment%20-%20Order`}
                  className="sd__pop-link sd__pop-link--email"
                >
                  {ORDERS_EMAIL}
                </a>
              </td>
            </tr>
            <tr>
              <th>Step 3</th>
              <td>
                Your order will be <strong>processed and confirmed</strong> once payment
                has been verified.
              </td>
            </tr>
          </tbody>
        </table>

        {/* Delivery summary */}
        <div className="sd__pop-delivery">
          <div className="sd__pop-delivery__head">
            <FiTruck size={12} /> Delivery &amp; Collection
          </div>
          <div className="sd__pop-delivery__rows">
            <div className="sd__pop-delivery__row">
              <span>Collection in store</span>
              <span className="sd__pop-delivery__free">FREE</span>
            </div>
            <div className="sd__pop-delivery__row">
              <span>Nationwide delivery</span>
              <span>R 250.00</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   SHOP DETAIL PAGE
   ══════════════════════════════════════ */
export default function ShopDetail() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const { addToCart, cart } = useCart();

  const [product,        setProduct]        = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [qty,            setQty]            = useState(1);
  const [mainImg,        setMainImg]        = useState(0);
  const [added,          setAdded]          = useState(false);
  const [tab,            setTab]            = useState("details");
  const [selectedMake,   setSelectedMake]   = useState("");
  const [socialLinks,    setSocialLinks]    = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [relatedProducts,setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [lightboxOpen,   setLightboxOpen]   = useState(false);

  const rvScrollRef = useRef(null);

  const [searchDraft, setSearchDraft] = useState("");
  const searchTimer = useRef(null);

  const { wishlistIds, toggle: toggleWishlist, isLoggedIn } = useWishlist();

  /* ── Fetch Product ── */
  useEffect(() => {
    setLoading(true);
    setMainImg(0);
    setQty(1);
    setAdded(false);
    setTab("details");
    setRelatedProducts([]);
    setLightboxOpen(false);

    fetch(`${process.env.REACT_APP_API_URL}/api/shop/products/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setProduct(data);
        if (data.car_make) setSelectedMake(data.car_make);
        setLoading(false);
        rvPush(data);
        const rv = rvRead().filter(p => p.slug !== slug).slice(0, RV_SHOW);
        setRecentlyViewed(rv);
      })
      .catch(() => { setLoading(false); navigate("/shop"); });
  }, [slug, navigate]);

  /* ── Fetch Related Products ── */
  useEffect(() => {
    if (!slug) return;
    setRelatedLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/shop/products/${slug}/related?limit=6`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setRelatedProducts(Array.isArray(data) ? data : []);
        setRelatedLoading(false);
      })
      .catch(() => setRelatedLoading(false));
  }, [slug]);

  /* ── Fetch Social Links ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/social-links`)
      .then(r => r.json())
      .then(data => setSocialLinks(Array.isArray(data) ? data.filter(s => s.active) : []))
      .catch(() => {});
  }, []);

  /* ── Search Handlers ── */
  const handleSearch = (e) => {
    const v = e.target.value;
    setSearchDraft(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (v.trim()) {
        navigate(`/shop?search=${encodeURIComponent(v.trim())}`);
      } else {
        navigate("/shop");
      }
    }, 450);
  };

  const handleSearchClear = () => {
    setSearchDraft("");
    navigate("/shop");
  };

  /* ── RV Scroll helpers ── */
  const rvScroll = (dir) => {
    if (!rvScrollRef.current) return;
    const card = rvScrollRef.current.querySelector(".sd__mini-card");
    const w    = card ? card.offsetWidth + 16 : 220;
    rvScrollRef.current.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  /* ── Loading Skeleton ── */
  if (loading) {
    return (
      <main className="sd">
        <div className="shop-hero">
          <div className="shop-hero__bg-image" />
          <div className="shop-hero__overlay" />
          <div className="container shop-hero__inner">
            <div className="shop-hero__left">
              <div className="shop-hero__tag">SPARE PARTS STORE</div>
              <h1 className="shop-hero__title">
                FIND THE RIGHT<br /><span>PART FOR YOUR CAR</span>
              </h1>
              <p className="shop-hero__sub">
                Quality spare parts &amp; accessories — delivered nationwide
              </p>
            </div>
          </div>
          <div className="shop-hero__mini-topbar">
            <ShopTopBar />
          </div>
        </div>

        <div className="container sd__skeleton-wrap">
          <div className="sd__ske sd__ske--img" />
          <div className="sd__ske-body">
            <div className="sd__ske sd__ske--title" />
            <div className="sd__ske sd__ske--line" />
            <div className="sd__ske sd__ske--line" />
            <div className="sd__ske sd__ske--price" />
          </div>
        </div>
      </main>
    );
  }

  if (!product) return null;

  /* ── Derived Values ── */
  const images = [
    product.image1,
    product.image2,
    product.image3,
    product.image4,
    product.image5,
  ].filter(Boolean);

  const price      = Number(product.sale_price || product.price);
  const oldPrice   = product.sale_price ? Number(product.price) : null;
  const inCart     = cart.some(i => i.id === product.id);
  const outOfStock = product.quantity < 1;
  const lowStock   = product.quantity > 0 && product.quantity <= (product.low_stock_alert || 5);
  const cleanDescription = stripHtml(product.description);

  const handleAdd = () => {
    if (outOfStock) return;
    addToCart({ ...product, selectedMake }, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const specs = [
    { label: "SKU",          val: product.sku },
    { label: "Part Number",  val: product.part_number },
    { label: "OEM Number",   val: product.oem_number },
    { label: "Condition",    val: product.condition_type },
    { label: "Placement",    val: product.placement },
    { label: "Weight",       val: product.weight ? `${product.weight} kg` : null },
    { label: "Dimensions",   val: product.dimensions },
    { label: "Fuel Type",    val: product.fuel_type },
    { label: "Transmission", val: product.transmission },
    { label: "Drive Type",   val: product.drive_type },
    { label: "Engine",       val: product.engine_size },
  ].filter(s => s.val);

  const makes = product.car_make ? [product.car_make] : CAR_MAKES;

  return (
    <main className="sd">

      {/* ─── Hero Banner ─── */}
      <div className="shop-hero">
        <div className="shop-hero__bg-image" />
        <div className="shop-hero__overlay" />

        <div className="container shop-hero__inner">
          <div className="shop-hero__left">
            <div className="shop-hero__tag">SPARE PARTS STORE</div>
            <h1 className="shop-hero__title">
              FIND THE RIGHT<br /><span>PART FOR YOUR CAR</span>
            </h1>
            <p className="shop-hero__sub">
              Quality spare parts &amp; accessories — delivered nationwide
            </p>
          </div>
        </div>

        <div className="shop-hero__mini-topbar">
          <ShopTopBar
            searchProps={{
              value:    searchDraft,
              onChange: handleSearch,
              onClear:  handleSearchClear,
            }}
          />
        </div>
      </div>

      {/* ─── Breadcrumb ─── */}
      <div className="sd__crumb-bar">
        <div className="container sd__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={12} />
          <Link to="/shop">Shop</Link>
          <FiArrowRight size={12} />
          {product.category_name && (
            <>
              <Link to={`/shop?category=${product.category_slug}`}>{product.category_name}</Link>
              <FiArrowRight size={12} />
            </>
          )}
          <span>{product.name}</span>
        </div>
      </div>

      {/* ─── Main Body ─── */}
      {/*
        Desktop grid (3 cols): images col | info col | trust panel
        Mobile order via CSS order property:
          1. images   (order: 1)
          2. info     (order: 2)
          3. trust    (order: 4)  ← pushed below POP panel on mobile
          POP panel rendered separately after trust in DOM but
          uses order: 3 on mobile via .sd__pop-col class
      */}
      <div className="container sd__body">

        {/* Images + POP panel stacked in left column */}
        <div className="sd__left-col">
          {/* Images */}
          <div className="sd__images">
            <div
              className="sd__main-img-wrap sd__main-img-wrap--zoomable"
              onClick={() => images.length > 0 && setLightboxOpen(true)}
              title="Click to zoom"
            >
              {images.length > 0 ? (
                <img
                  src={shopImg(images[mainImg])}
                  alt={product.name}
                  className="sd__main-img"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="sd__no-img"><FiPackage size={80} /></div>
              )}

              {product.discount_percent > 0 && (
                <span className="sd__badge">-{Math.round(product.discount_percent)}%</span>
              )}

              {images.length > 0 && (
                <div className="sd__zoom-hint" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    className="sd__img-nav sd__img-nav--prev"
                    onClick={(e) => { e.stopPropagation(); setMainImg(i => (i - 1 + images.length) % images.length); }}
                  >
                    <FiChevronLeft />
                  </button>
                  <button
                    className="sd__img-nav sd__img-nav--next"
                    onClick={(e) => { e.stopPropagation(); setMainImg(i => (i + 1) % images.length); }}
                  >
                    <FiChevronRight />
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="sd__thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`sd__thumb ${mainImg === i ? "sd__thumb--active" : ""}`}
                    onClick={() => setMainImg(i)}
                  >
                    <img
                      src={shopImg(img)}
                      alt={`View ${i + 1}`}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Payment Instructions Panel — below images on desktop */}
          <PaymentInstructionsPanel />
        </div>

        {/* Product Info */}
        <div className="sd__info">
          {product.category_name && (
            <Link to={`/shop?category=${product.category_slug}`} className="sd__cat">
              <FiTag size={12} /> {product.category_name}
            </Link>
          )}

          <h1 className="sd__title">{product.name}</h1>

          {product.car_make && (
            <p className="sd__fitment">
              Fits: <strong>{product.car_make} {product.car_model}</strong>
              {product.car_year_from && ` (${product.car_year_from}${product.car_year_to ? `–${product.car_year_to}` : "+"})`}
              {product.car_variant && ` · ${product.car_variant}`}
            </p>
          )}

          <div className="sd__price-row">
            <span className="sd__price">{fmt(price)}</span>
            {oldPrice && <span className="sd__old-price">{fmt(oldPrice)}</span>}
            {product.discount_percent > 0 && (
              <span className="sd__save">Save {Math.round(product.discount_percent)}%</span>
            )}
          </div>

          <div className={`sd__stock ${outOfStock ? "sd__stock--out" : lowStock ? "sd__stock--low" : "sd__stock--in"}`}>
            {outOfStock
              ? <><FiAlertTriangle size={14} /> Out of stock</>
              : lowStock
              ? <><FiAlertTriangle size={14} /> Only {product.quantity} left</>
              : <><FiCheck size={14} /> In stock ({product.quantity} available)</>
            }
          </div>

          {cleanDescription && (
            <p className="sd__desc">
              {cleanDescription.slice(0, 200)}{cleanDescription.length > 200 ? "…" : ""}
            </p>
          )}

          {/* Car Make Selector */}
          <div className="sd__makes-section">
            <div className="sd__makes-label">
              Select Vehicle Make: {selectedMake && <strong>{selectedMake}</strong>}
            </div>
            <div className="sd__makes-grid">
              {makes.map(make => (
                <button
                  key={make}
                  className={`sd__make-btn ${selectedMake === make ? "sd__make-btn--active" : ""}`}
                  onClick={() => setSelectedMake(make === selectedMake ? "" : make)}
                >
                  {make}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity & Add to Cart */}
          <div className="sd__actions">
            <div className="sd__qty">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}>
                <FiMinus size={14} />
              </button>
              <span>{qty}</span>
              <button onClick={() => setQty(q => Math.min(product.quantity, q + 1))} disabled={qty >= product.quantity}>
                <FiPlus size={14} />
              </button>
            </div>
            <button
              className={`sd__add-btn ${added ? "sd__add-btn--added" : ""}`}
              onClick={handleAdd}
              disabled={outOfStock}
            >
              {added
                ? <><FiCheck size={16} /> Added!</>
                : <><FiShoppingCart size={16} /> Add to Cart</>
              }
            </button>
          </div>

          {/* Wishlist Button */}
          <div className="sd__wishlist-row">
            <WishlistBtn
              productId={product.id}
              wishlistIds={wishlistIds}
              toggle={toggleWishlist}
              isLoggedIn={isLoggedIn}
            />
          </div>

          {inCart && !added && (
            <Link to="/cart" className="sd__view-cart">View cart →</Link>
          )}

          {/* Share */}
          {socialLinks.length > 0 && (
            <div className="sd__share">
              <span className="sd__share-label"><FiShare2 size={13} /> Share:</span>
              <div className="sd__share-icons">
                {socialLinks.map(s => (
                  <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="sd__share-icon" title={s.platform}>
                    <SocialIcon platform={s.platform} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Trust Panel */}
        <div className="sd__trust-panel">
          {TRUST_ITEMS.map((item, i) => (
            <div key={i} className="sd__trust-item">
              <div className="sd__trust-icon">{item.icon}</div>
              <div className="sd__trust-text">
                <strong>{item.title}</strong>
                <span>{item.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div className="container">
        <div className="sd__tabs">
          <button
            className={`sd__tab ${tab === "details" ? "active" : ""}`}
            onClick={() => setTab("details")}
          >
            Product Details
          </button>
          {product.compatibility_notes && (
            <button
              className={`sd__tab ${tab === "compat" ? "active" : ""}`}
              onClick={() => setTab("compat")}
            >
              Compatibility
            </button>
          )}
        </div>

        <div className="sd__tab-body">
          {tab === "details" && (
            <div className="sd__specs">
              {cleanDescription && (
                <div className="sd__spec-desc">
                  <h4>Description</h4>
                  <p>{cleanDescription}</p>
                </div>
              )}
              {specs.length > 0 && (
                <table className="sd__spec-table">
                  <tbody>
                    {specs.map(s => (
                      <tr key={s.label}>
                        <th>{s.label}</th>
                        <td>{s.val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {tab === "compat" && (
            <div className="sd__compat">
              <p>{product.compatibility_notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* ─── Mobile-only Payment Instructions ─── */}
      {/* On mobile, the left-col POP panel is hidden and this one is shown instead,
          sitting between the tabs section and the trust panel mobile repeat.
          We achieve reordering purely via CSS on the sd__body grid children. */}
      <div className="container sd__pop-mobile">
        <PaymentInstructionsPanel />
      </div>

      {/* ══════════════════════════════════════════════════════════
          RECENTLY VIEWED SECTION
          ══════════════════════════════════════════════════════════ */}
      {recentlyViewed.length > 0 && (
        <section className="sd__rv-section">
          <div className="container">
            <div className="sd__section-header">
              <div className="sd__section-header-left">
                <span className="sd__section-icon"><FiClock size={18} /></span>
                <h2 className="sd__section-title">Recently Viewed</h2>
              </div>
              <Link to="/shop" className="sd__section-link">
                Browse all <FiArrowRight size={13} />
              </Link>
            </div>
          </div>

          <div className="sd__rv-outer">
            {recentlyViewed.length > 3 && (
              <>
                <button
                  className="sd__rv-arrow sd__rv-arrow--left"
                  onClick={() => rvScroll(-1)}
                  aria-label="Scroll left"
                >
                  <FiChevronLeft size={20} />
                </button>
                <button
                  className="sd__rv-arrow sd__rv-arrow--right"
                  onClick={() => rvScroll(1)}
                  aria-label="Scroll right"
                >
                  <FiChevronRight size={20} />
                </button>
              </>
            )}

            <div className="container">
              <div className="sd__rv-track" ref={rvScrollRef}>
                {recentlyViewed.map(p => (
                  <MiniCard key={p.id} product={p} variant="rv" />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════
          RELATED PRODUCTS SECTION
          ══════════════════════════════════════════════════════════ */}
      {(relatedLoading || relatedProducts.length > 0) && (
        <section className="sd__related-section">
          <div className="container">
            <div className="sd__section-header">
              <div className="sd__section-header-left">
                <span className="sd__section-icon"><FiGrid size={18} /></span>
                <h2 className="sd__section-title">
                  {product.category_name
                    ? `More from ${product.category_name}`
                    : "You May Also Like"}
                </h2>
              </div>
              {product.category_slug && (
                <Link
                  to={`/shop?category=${product.category_slug}`}
                  className="sd__section-link"
                >
                  View category <FiArrowRight size={13} />
                </Link>
              )}
            </div>

            {relatedLoading ? (
              <div className="sd__related-grid">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="sd__related-ske">
                    <div className="sd__ske sd__ske--rel-img" />
                    <div className="sd__ske sd__ske--rel-name" />
                    <div className="sd__ske sd__ske--rel-price" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="sd__related-grid">
                {relatedProducts.map(p => (
                  <MiniCard key={p.id} product={p} variant="related" />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Back Button ─── */}
      <div className="sd__back-row container">
        <Link to="/shop" className="sd__back">
          <FiChevronLeft size={16} /> Back to Shop
        </Link>
      </div>

      {/* ─── Lightbox Modal ─── */}
      {lightboxOpen && images.length > 0 && (
        <LightboxModal
          images={images}
          startIndex={mainImg}
          onClose={() => setLightboxOpen(false)}
        />
      )}

    </main>
  );
}